'use client'

import React from 'react'
import { CollectionArchive } from '@/ui/components/CollectionArchive'
import { BaseGatedArchiveClientProps } from '@/ui/blocks/shared/gatedArchiveTypes'
import { CardType } from '@/ui/components/Card/types'
import { mapResourcesToCardProps } from './mapper'
import { SortingOption } from '@/ui/components/SortBy'

/**
 * Props for ResourcesArchiveClient
 */
export interface ResourcesArchiveClientProps
  extends Omit<
    BaseGatedArchiveClientProps,
    'title' | 'titleHierarchy' | 'introContent' | 'fixedTitleStyle'
  > {
  /** Sorting options for the archive */
  sortingOptions?: SortingOption[]
  /** Filter appearance style */
  filterAppearance?: 'default' | 'compact' | 'grid'
}

/**
 * Client component for ResourcesArchive
 *
 * Simplified client component that delegates all logic to CollectionArchive.
 * All gating, authentication, and rendering logic is now handled by the
 * generic CollectionArchive component.
 */
export const ResourcesArchiveClient: React.FC<ResourcesArchiveClientProps> = ({
  initialData,
  fetchCollectionDataAction,
  isGated,
  gateConfig,
  where,
  locale,
  limit,
  gridCols,
  filters = [],
  filterField = 'resourceType',
  filterTitle,
  archiveId = 'resources',
  sortingOptions,
  filterAppearance = 'grid',
  emptyStateType,
  emptyStateButton,
  emptyStateHasButton,
}) => {
  // All logic delegated to CollectionArchive
  return (
    <CollectionArchive
      archiveId={archiveId}
      initialData={initialData}
      fetchCollectionDataAction={fetchCollectionDataAction}
      collection="resources"
      locale={locale}
      where={where}
      limit={limit}
      gridCols={gridCols}
      filter={filters}
      filterField={filterField}
      filterTitle={filterTitle}
      cardType={CardType.Resource}
      mapper={mapResourcesToCardProps}
      loadMoreStrategy="pagination"
      sortingOptions={sortingOptions}
      filterAppearance={filterAppearance}
      // Gating props
      isGated={isGated}
      gateConfig={gateConfig}
      // Empty state props
      emptyStateType={emptyStateType}
      emptyStateButton={emptyStateButton}
      emptyStateHasButton={emptyStateHasButton}
    />
  )
}
